import React, { useState, useMemo } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AddIcon from '@mui/icons-material/Add';

export default function TaxiOwners() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();

  // Placeholder data - will be replaced with real data
  const [data] = useState([
    {
      id: 1,
      ownerName: 'Oslo Taxi AS',
      companyName: 'Oslo Taxi AS',
      organizationNumber: '987654321',
      phone: '+47 22 00 00 00',
      email: 'post@oslotaxi.no',
      totalVehicles: 12,
      activeVehicles: 10,
    },
    {
      id: 2,
      ownerName: 'Bergen Drosje',
      companyName: 'Bergen Drosje AS',
      organizationNumber: '123456789',
      phone: '+47 55 00 00 00',
      email: 'info@bergendrosje.no',
      totalVehicles: 8,
      activeVehicles: 7,
    },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(d =>
      (d.ownerName && d.ownerName.toLowerCase().includes(query)) ||
      (d.companyName && d.companyName.toLowerCase().includes(query)) ||
      (d.organizationNumber && d.organizationNumber.includes(query)) ||
      (d.email && d.email.toLowerCase().includes(query))
    );
  }, [data, searchQuery]);

  // Summary stats
  const summaryData = useMemo(() => ({
    totalOwners: data.length,
    totalVehicles: data.reduce((sum, d) => sum + (d.totalVehicles || 0), 0),
    activeVehicles: data.reduce((sum, d) => sum + (d.activeVehicles || 0), 0),
  }), [data]);

  const columns = [
    {
      title: t('owner_name') || 'Owner Name',
      field: 'ownerName',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BusinessIcon sx={{ color: '#6366F1', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
              {rowData.ownerName || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {rowData.companyName || '-'}
            </Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('organization_number') || 'Org. Number',
      field: 'organizationNumber',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151', fontFamily: 'monospace' }}>
          {rowData.organizationNumber || '-'}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('contact') || 'Contact',
      field: 'phone',
      render: rowData => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 14, color: '#6B7280' }} />
            <Typography sx={{ fontSize: 13, color: '#374151' }}>{rowData.phone || '-'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EmailIcon sx={{ fontSize: 14, color: '#6B7280' }} />
            <Typography sx={{ fontSize: 13, color: '#374151' }}>{rowData.email || '-'}</Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('total_vehicles') || 'Vehicles',
      field: 'totalVehicles',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DirectionsCarIcon sx={{ fontSize: 18, color: '#6B7280' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
            {rowData.totalVehicles || 0}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    {
      title: t('active_vehicles') || 'Active',
      field: 'activeVehicles',
      render: rowData => (
        <Chip
          label={rowData.activeVehicles || 0}
          size="small"
          sx={{
            bgcolor: '#DCFCE7',
            color: '#16A34A',
            fontWeight: 600,
            fontSize: 13,
          }}
        />
      ),
      cellStyle: { padding: '14px 16px' },
    },
  ];

  return (
    <Box style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('taxi_owners') || 'Taxi Owners'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('taxi_owners_description') || 'Manage taxi owners and their fleets'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            bgcolor: '#111827',
            color: '#FFFFFF',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            '&:hover': { bgcolor: '#374151' },
          }}
        >
          {t('add_owner') || 'Add Owner'}
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PersonIcon sx={{ color: '#6366F1', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('total_owners') || 'Total Owners'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {summaryData.totalOwners}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DirectionsCarIcon sx={{ color: '#2563EB', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('total_vehicles') || 'Total Vehicles'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {summaryData.totalVehicles}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DirectionsCarIcon sx={{ color: '#16A34A', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('active_vehicles') || 'Active Vehicles'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#16A34A' }}>
              {summaryData.activeVehicles}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Filter Toolbar */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <TextField
          size="small"
          placeholder={t('search') || 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 280,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#F9FAFB',
            },
          }}
        />
        <Typography sx={{ fontSize: 13, color: '#6B7280', ml: 'auto' }}>
          <strong style={{ color: '#111827' }}>{filteredData.length}</strong> {t('results') || 'results'}
        </Typography>
      </Box>

      {/* Table */}
      <MaterialTable
        icons={TableIcons}
        title={null}
        columns={columns}
        data={filteredData}
        onRowClick={(evt, selectedRow) => setSelectedRow(selectedRow.tableData.id)}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: 12,
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
        options={{
          ...tableOptions,
          search: false,
          toolbar: false,
          showTitle: false,
          rowStyle: rowData => ({
            backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
          }),
        }}
        localization={{
          body: { emptyDataSourceMessage: t('no_data') || 'No data to display' },
          pagination: {
            labelDisplayedRows: '{from}-{to} ' + (t('of') || 'of') + ' {count}',
            firstTooltip: t('first_page_tooltip'),
            previousTooltip: t('previous_page_tooltip'),
            nextTooltip: t('next_page_tooltip'),
            lastTooltip: t('last_page_tooltip'),
          },
        }}
      />
    </Box>
  );
}
